import Parser from 'rss-parser';


interface RSSFeedSource {
  id: string;
  name: string;
  url: string;
  category: string;
  country: string;
  enabled: boolean;
}

interface RSSArticle {
  id: string;
  title: string;
  summary: string;
  image: string;
  imageAlt: string;
  source: {
    name: string;
    logo: string;
    logoAlt: string;
    url: string;
  };
  category: string;
  timestamp: string;
  readTime: string;
  country: string;
  externalUrl: string;
  publishedAt: string;
}

const parser = new Parser({
  customFields: {
    item: [
      ['media:content', 'mediaContent'],
      ['media:thumbnail', 'mediaThumbnail'],
      ['enclosure', 'enclosure'],
    ],
  },
});

/**
 * Fetch and parse RSS feed from a given URL
 */
export async function fetchRSSFeed(feedUrl: string, sourceName: string, category: string, country: string): Promise<RSSArticle[]> {
  try {
    // Use API route to fetch RSS feed server-side (bypasses CORS)
    const response = await fetch(`/api/rss?url=${encodeURIComponent(feedUrl)}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data.success || !data.feed) {
      throw new Error(data.error || 'Failed to fetch RSS feed');
    }
    
    const feed = data.feed;
    
    return feed.items.map((item: any, index: number) => {
      // Extract image from various RSS formats
      let imageUrl = '/assets/images/no_image.png';
      
      if (item.enclosure?.url) {
        imageUrl = item.enclosure.url;
      } else if (item['media:content']?.$?.url) {
        imageUrl = item['media:content'].$.url;
      } else if (item['media:thumbnail']?.$?.url) {
        imageUrl = item['media:thumbnail'].$.url;
      } else if (item.content && item.content.includes('<img')) {
        const imgMatch = item.content.match(/<img[^>]+src="([^"]+)"/); 
        if (imgMatch) imageUrl = imgMatch[1];
      }

      const publishedDate = item.pubDate ? new Date(item.pubDate) : new Date();
      const timestamp = getRelativeTime(publishedDate);

      return {
        id: `rss_${sourceName}_${index}_${Date.now()}`,
        title: item.title || 'Untitled',
        summary: item.contentSnippet || item.content?.substring(0, 200) || 'No summary available',
        image: imageUrl,
        imageAlt: `Image for ${item.title}`,
        source: {
          name: sourceName,
          logo: 'https://img.rocket.new/generatedImages/rocket_gen_img_1a6486767-1770656497074.png',
          logoAlt: `${sourceName} logo`,
          url: item.link || feedUrl,
        },
        category: category,
        timestamp: timestamp,
        readTime: '2 min',
        country: country,
        externalUrl: item.link || '',
        publishedAt: publishedDate.toISOString(),
      };
    });
  } catch (error) {
    console.error(`Error fetching RSS feed from ${feedUrl}:`, error);
    return [];
  }
}

/**
 * Fetch articles from multiple RSS feeds
 */
export async function fetchMultipleRSSFeeds(sources: RSSFeedSource[]): Promise<RSSArticle[]> {
  const enabledSources = sources.filter(source => source.enabled);
  
  const feedPromises = enabledSources.map(source =>
    fetchRSSFeed(source.url, source.name, source.category, source.country)
  );

  const results = await Promise.allSettled(feedPromises);
  
  const allArticles: RSSArticle[] = [];
  results.forEach(result => {
    if (result.status === 'fulfilled') {
      allArticles.push(...result.value);
    }
  });

  // Sort by publication date
  allArticles.sort((a, b) => {
    const dateA = new Date(a.publishedAt).getTime();
    const dateB = new Date(b.publishedAt).getTime();
    return dateB - dateA;
  });

  return allArticles;
}

/**
 * Get relative time string
 */
function getRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 60) {
    return `${diffMins} mins ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  } else {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  }
}

/**
 * Default RSS feed sources (can be managed via admin panel)
 */
export const DEFAULT_RSS_SOURCES: RSSFeedSource[] = [
  {
    id: 'bbc-world',
    name: 'BBC News',
    url: 'http://feeds.bbci.co.uk/news/world/rss.xml',
    category: 'World',
    country: 'WORLD',
    enabled: true,
  },
  {
    id: 'cnn-world',
    name: 'CNN World',
    url: 'http://rss.cnn.com/rss/edition_world.rss',
    category: 'World',
    country: 'WORLD',
    enabled: true,
  },
  {
    id: 'reuters-world',
    name: 'Reuters World',
    url: 'https://www.reutersagency.com/feed/?taxonomy=best-topics&post_type=best',
    category: 'World',
    country: 'WORLD',
    enabled: false, // Disabled due to 500 errors
  },
  {
    id: 'techcrunch',
    name: 'TechCrunch',
    url: 'https://techcrunch.com/feed/',
    category: 'Technology',
    country: 'WORLD',
    enabled: true,
  },
  {
    id: 'nyt-world',
    name: 'New York Times',
    url: 'https://rss.nytimes.com/services/xml/rss/nyt/World.xml',
    category: 'World',
    country: 'US',
    enabled: true,
  },
];